import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Switch,
  Modal,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { headstoneDetailsService } from '../../../../services/headstoneDetailsInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('HeadstoneDetailsInput');

export const HeadstoneDetailsInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { headstoneDetailsId?: string; isEdit?: boolean }>, string>>();
  const { headstoneDetailsId, isEdit } = route.params || {};
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedHeadstoneDetailsId, setSavedHeadstoneDetailsId] = useState<string | undefined>();

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  // Helper: format currency as $x,xxx,xxx while typing
  const formatCurrency = (text: string) => {
    // Remove all non-digit characters except for existing dollar sign
    const cleaned = text.replace(/[^\d]/g, '');

    if (cleaned === '') return '';

    // Convert to number and format with commas
    const number = parseInt(cleaned, 10);
    const formatted = number.toLocaleString();

    return `$${formatted}`;
  };

  const [formData, setFormData] = useState({
    headstonePreference: '',
    materialPreference: '',
    sizePreference: '',
    stylePreference: '',
    colorPreference: '',
    inscriptionText: '',
    epitaph: '',
    symbolsImagery: '',
    fontStyle: '',
    datesFormat: '',
    additionalEngravings: '',
    monumentCompany: '',
    companyContact: '',
    budgetRange: '',
    notes: '',
    username: '',
    password: '',
  });

  // Load existing headstone details data when editing
  useEffect(() => {
    logger.debug('[Edit Debug] useEffect triggered - isEdit:', isEdit, 'headstoneDetailsId:', headstoneDetailsId);
    if (isEdit && headstoneDetailsId) {
      logger.debug('[Edit Debug] Loading headstone details data for editing');
      loadHeadstoneDetailsData();
    }
  }, [isEdit, headstoneDetailsId]);

  const loadHeadstoneDetailsData = async () => {
    try {
      logger.debug('[Edit Debug] loadHeadstoneDetailsData called with ID:', headstoneDetailsId);
      if (!headstoneDetailsId) {
        logger.debug('[Edit Debug] No headstoneDetailsId provided, returning');
        return;
      }

      logger.debug('[Edit Debug] Fetching data for ID:', headstoneDetailsId);
      const data = await headstoneDetailsService.getHeadstoneDetailsById(headstoneDetailsId);
      logger.debug('[Edit Debug] Received data:', data);

      if (data) {
        setFormData({
          headstonePreference: data.headstonePreference || '',
          materialPreference: data.materialPreference || '',
          sizePreference: data.sizePreference || '',
          stylePreference: data.stylePreference || '',
          colorPreference: data.colorPreference || '',
          inscriptionText: data.inscriptionText || '',
          epitaph: data.epitaph || '',
          symbolsImagery: data.symbolsImagery || '',
          fontStyle: data.fontStyle || '',
          datesFormat: data.datesFormat || '',
          additionalEngravings: data.additionalEngravings || '',
          monumentCompany: data.monumentCompany || '',
          companyContact: formatPhoneNumber(data.companyContact || ''),
          budgetRange: data.budgetRange ? formatCurrency(data.budgetRange) : '',
          notes: data.notes || '',
          username: '',
          password: '',
        });

        // Load portal credentials
        try {
          const portalData = await vaultService.getPortalCredentials(headstoneDetailsId, 'headstone-details');
          if (portalData) {
            setFormData(prev => ({
              ...prev,
              username: portalData.username || '',
              password: portalData.password || '',
            }));
            setHasOnlinePortal(true);
          }
        } catch (vaultError) {
          logger.debug('Could not load portal credentials:', vaultError);
        }
      }
    } catch (error) {
      logger.error('Error loading headstone details data:', error);
      Alert.alert('Error', 'Failed to load headstone details information');
    }
  };

  const handleBudgetChange = (text: string) => {
    const formatted = formatCurrency(text);
    setFormData({ ...formData, budgetRange: formatted });
  };

  const handlePhoneChange = (text: string) => {
    const formatted = formatPhoneNumber(text);
    setFormData({ ...formData, companyContact: formatted });
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }

      // Basic validation
      if (!formData.headstonePreference) {
        Alert.alert('Validation Error', 'Headstone preference is required');
        setSubmitting(false);
        return;
      }

      // Prepare payload for API
      const payload: any = {
        headstonePreference: formData.headstonePreference || undefined,
        materialPreference: formData.materialPreference || undefined,
        sizePreference: formData.sizePreference || undefined,
        stylePreference: formData.stylePreference || undefined,
        colorPreference: formData.colorPreference || undefined,
        inscriptionText: formData.inscriptionText || undefined,
        epitaph: formData.epitaph || undefined,
        symbolsImagery: formData.symbolsImagery || undefined,
        fontStyle: formData.fontStyle || undefined,
        datesFormat: formData.datesFormat || undefined,
        additionalEngravings: formData.additionalEngravings || undefined,
        monumentCompany: formData.monumentCompany || undefined,
        companyContact: formData.companyContact.replace(/\D/g, '') || undefined,
        budgetRange: formData.budgetRange || undefined,
        notes: formData.notes || undefined,
      };

      // Create or update headstone details record
      let saved;
      let savedId;

      if (isEdit && headstoneDetailsId) {
        saved = await headstoneDetailsService.updateHeadstoneDetails(headstoneDetailsId, payload);
        savedId = headstoneDetailsId;
      } else {
        saved = await headstoneDetailsService.createHeadstoneDetails(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
      }

      // Store the saved ID for the Done button
      setSavedHeadstoneDetailsId(savedId);

      // Store portal credentials in Vault if provided
      if (savedId && (formData.username || formData.password)) {
        try {
          await vaultService.storePortalCredentials(savedId, 'headstone-details', {
            username: formData.username,
            password: formData.password,
          });
        } catch (e) {
          logger.error('Vault write failed for headstone details portal credentials:', e);
        }
      }

      if (isEdit) {
        // For edit mode, navigate to lock/unlock screen
        Alert.alert('Success', 'Headstone details information updated successfully!', [
          {
            text: 'OK',
            onPress: () => navigation.navigate('HeadstoneDetailsLockUnlock', { headstoneDetailsId: headstoneDetailsId })
          }
        ]);
      } else {
        // Clear the form and reset states for Add Another
        setFormData({
          headstonePreference: '',
          materialPreference: '',
          sizePreference: '',
          stylePreference: '',
          colorPreference: '',
          inscriptionText: '',
          epitaph: '',
          symbolsImagery: '',
          fontStyle: '',
          datesFormat: '',
          additionalEngravings: '',
          monumentCompany: '',
          companyContact: '',
          budgetRange: '',
          notes: '',
          username: '',
          password: '',
        });
        setHasOnlinePortal(false);

        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
        // Reset submitting so buttons are interactive
        setSubmitting(false);
      }
    } catch (error: any) {
      logger.error('Error saving headstone details info:', error);
      Alert.alert('Error', 'Failed to save headstone details information. Please try again.');
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFormData({
      headstonePreference: '',
      materialPreference: '',
      sizePreference: '',
      stylePreference: '',
      colorPreference: '',
      inscriptionText: '',
      epitaph: '',
      symbolsImagery: '',
      fontStyle: '',
      datesFormat: '',
      additionalEngravings: '',
      monumentCompany: '',
      companyContact: '',
      budgetRange: '',
      notes: '',
      username: '',
      password: '',
    });
    setHasOnlinePortal(false);
    setShowAddAnotherDone(false);
    setSubmitting(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    logger.debug('[Done Debug] Using ID:', savedHeadstoneDetailsId);
    (navigation as any).navigate('HeadstoneDetailsLockUnlock', { headstoneDetailsId: savedHeadstoneDetailsId });
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Headstone Details Info</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Headstone Preference *</Text>
            <TextInput
              style={styles.input}
              value={formData.headstonePreference}
              onChangeText={(text) => setFormData({ ...formData, headstonePreference: text })}
              placeholder="Ex: Yes, No, Marker Only"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Material Preference</Text>
            <TextInput
              style={styles.input}
              value={formData.materialPreference}
              onChangeText={(text) => setFormData({ ...formData, materialPreference: text })}
              placeholder="Ex: Granite, Marble, Bronze"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Size Preference</Text>
            <TextInput
              style={styles.input}
              value={formData.sizePreference}
              onChangeText={(text) => setFormData({ ...formData, sizePreference: text })}
              placeholder="Ex: 24x12, 36x18"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Style Preference</Text>
            <TextInput
              style={styles.input}
              value={formData.stylePreference}
              onChangeText={(text) => setFormData({ ...formData, stylePreference: text })}
              placeholder="Ex: Upright, Flat, Slant"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Color Preference</Text>
            <TextInput
              style={styles.input}
              value={formData.colorPreference}
              onChangeText={(text) => setFormData({ ...formData, colorPreference: text })}
              placeholder="Ex: Black, Gray, Pink"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Inscription Text</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.inscriptionText}
              onChangeText={(text) => setFormData({ ...formData, inscriptionText: text })}
              placeholder="Enter the text you want on your headstone"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Epitaph</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.epitaph}
              onChangeText={(text) => setFormData({ ...formData, epitaph: text })}
              placeholder="A meaningful quote or verse"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={3}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Symbols or Imagery</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.symbolsImagery}
              onChangeText={(text) => setFormData({ ...formData, symbolsImagery: text })}
              placeholder="Ex: Cross, Star of David, Flowers, Angels"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={2}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Font Style</Text>
            <TextInput
              style={styles.input}
              value={formData.fontStyle}
              onChangeText={(text) => setFormData({ ...formData, fontStyle: text })}
              placeholder="Ex: Script, Block, Traditional"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Dates Format</Text>
            <TextInput
              style={styles.input}
              value={formData.datesFormat}
              onChangeText={(text) => setFormData({ ...formData, datesFormat: text })}
              placeholder="Ex: Jan 1, 1950 - Dec 31, 2050"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Additional Engravings</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.additionalEngravings}
              onChangeText={(text) => setFormData({ ...formData, additionalEngravings: text })}
              placeholder="Any other text or details"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={3}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Monument Company</Text>
            <TextInput
              style={styles.input}
              value={formData.monumentCompany}
              onChangeText={(text) => setFormData({ ...formData, monumentCompany: text })}
              placeholder="Company name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Monument Company Phone Number</Text>
            <TextInput
              style={styles.input}
              value={formData.companyContact}
              onChangeText={handlePhoneChange}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Budget Range</Text>
            <TextInput
              style={styles.input}
              value={formData.budgetRange}
              onChangeText={handleBudgetChange}
              placeholder="Ex: $2,000"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          {/* Portal Toggle */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
            >
              <View style={[styles.toggle, hasOnlinePortal && styles.toggleActive]}>
                <View style={[styles.toggleThumb, hasOnlinePortal && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Does the monument company have an online portal?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              If yes, enter your login credentials. These will be securely stored in your vault and will only be shared with your successor after your End of Life event.
            </Text>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Additional notes or special instructions"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={styles.addAnotherButton}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.doneButton}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {submitting ? (
                <TouchableOpacity style={[styles.submitButton, styles.buttonDisabled]} disabled>
                  <ActivityIndicator color="#FFFFFF" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSubmit}
                >
                  <Text style={styles.submitButtonText}>
                    {isEdit ? 'Update' : 'Submit'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  vaultText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontWeight: '600',
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: ms(15),
    padding: ms(5),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
});

export default HeadstoneDetailsInputScreen;